import React from 'react'
import { useQuery } from 'react-query'
import { 
  FolderKanban, 
  Users, 
  Package, 
  Banknote, 
  CheckCircle, 
  Clock,
  AlertCircle
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { dashboardService } from '@/services/dashboardService'

const RecentActivities = () => {
  const { data: activitiesData, isLoading } = useQuery(
    'dashboard-recent-activities',
    dashboardService.getRecentActivities,
    {
      select: (response) => response.data,
      refetchInterval: 2 * 60 * 1000, // Refetch every 2 minutes
    }
  )

  const activities = activitiesData?.data?.activities || []
  
  const getActivityIcon = (action) => {
    const icons = {
      login: Users,
      logout: Users,
      create: FolderKanban,
      update: CheckCircle,
      delete: AlertCircle,
      view: Clock
    }
    return icons[action] || Clock
  }

  const getActivityColor = (action) => {
    const colors = {
      login: 'text-green-600 bg-green-50',
      logout: 'text-gray-600 bg-gray-50',
      create: 'text-blue-600 bg-blue-50',
      update: 'text-yellow-600 bg-yellow-50',
      delete: 'text-red-600 bg-red-50',
      view: 'text-purple-600 bg-purple-50'
    }
    return colors[action] || 'text-gray-600 bg-gray-50'
  }

  const displayActivities = activities

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <div className="animate-pulse">
            <div className="flex items-center justify-between mb-4">
              <div className="h-6 bg-gray-300 rounded w-1/3"></div>
              <div className="h-4 bg-gray-300 rounded w-16"></div>
            </div>
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex space-x-3">
                  <div className="h-8 w-8 bg-gray-300 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-300 rounded w-1/2 mb-1"></div>
                    <div className="h-3 bg-gray-300 rounded w-1/4"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Recent Activities</h3>
          <button className="text-sm text-primary-600 hover:text-primary-500">
            View all
          </button>
        </div>
        
        <div className="flow-root">
          <ul className="-mb-8">
            {displayActivities.slice(0, 5).map((activity, index) => {
              const Icon = getActivityIcon(activity.action)
              const colorClasses = getActivityColor(activity.action)
              const isLast = index === displayActivities.slice(0, 5).length - 1
              
              return (
                <li key={activity.id}>
                  <div className="relative pb-8">
                    {!isLast && (
                      <span 
                        className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" 
                        aria-hidden="true" 
                      />
                    )}
                    <div className="relative flex space-x-3">
                      <div>
                        <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${colorClasses}`}>
                          <Icon className="h-4 w-4" />
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div>
                          <div className="text-sm">
                            <span className="font-medium text-gray-900">
                              {activity.action} {activity.resource_type}
                            </span>
                          </div>
                          <p className="mt-0.5 text-sm text-gray-500">
                            {activity.description || `${activity.action} ${activity.resource_type} ${activity.resource_name || ''}`}
                          </p>
                        </div>
                        <div className="mt-2 text-sm text-gray-500">
                          <time dateTime={activity.created_at}>
                            {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                          </time>
                          {activity.PerformedBy && (
                            <span className="ml-2">by {activity.PerformedBy.first_name} {activity.PerformedBy.last_name}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      </div>
    </div>
  )
}

export default RecentActivities